import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Globe, FileEdit, LogOut, ExternalLink, Loader2 } from "lucide-react";
import { Logo } from "../../components/marketing/Logo";
import { adminApi, getToken, clearToken, formatApiError } from "./api";

export default function AdminBlog() {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await adminApi().get("/admin/blog/posts");
            setPosts(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                clearToken();
                navigate("/admin/login", { replace: true });
            } else {
                toast.error(formatApiError(err?.response?.data?.detail));
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        document.title = "Blog Manager | Kryvo Admin";
        if (!getToken()) {
            navigate("/admin/login", { replace: true });
            return;
        }
        load();
    }, [navigate, load]);

    const toggleStatus = async (post) => {
        const next = post.status === "published" ? "draft" : "published";
        setBusyId(post.id);
        try {
            await adminApi().put(`/admin/blog/posts/${post.id}`, { ...post, status: next });
            toast.success(next === "published" ? `"${post.title}" is now live.` : `"${post.title}" moved to drafts.`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (post) => {
        if (!window.confirm(`Delete "${post.title}" permanently?`)) return;
        setBusyId(post.id);
        try {
            await adminApi().delete(`/admin/blog/posts/${post.id}`);
            toast.success("Article deleted.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setBusyId(null);
        }
    };

    const logout = () => {
        clearToken();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div data-testid="admin-blog-dashboard" className="min-h-screen bg-[#F7F9FA]">
            <header className="border-b border-[#E5E7EB] bg-white">
                <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
                    <div className="flex items-center gap-3">
                        <Link to="/"><Logo /></Link>
                        <span className="rounded-full bg-[#F0FDF4] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#15803D]">
                            Blog Manager
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/blog" className="hidden items-center gap-1.5 rounded-full border border-[#E5E7EB] px-4 py-2 text-xs font-semibold text-[#475569] hover:text-[#15803D] sm:flex">
                            <ExternalLink className="h-3.5 w-3.5" /> View public blog
                        </Link>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-button"
                            className="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-4 py-2 text-xs font-semibold text-[#475569] hover:text-red-600"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-display text-2xl font-extrabold text-[#111827]">Articles</h1>
                        <p className="mt-1 text-sm text-[#64748B]">{posts.length} total · {posts.filter((p) => p.status === "published").length} published</p>
                    </div>
                    <Link
                        to="/admin/blog/new"
                        data-testid="admin-new-article-button"
                        className="flex items-center gap-2 rounded-full bg-[#16A34A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#15803D]"
                    >
                        <Plus className="h-4 w-4" /> New article
                    </Link>
                </div>

                {loading ? (
                    <p className="flex items-center justify-center gap-2 py-20 text-sm text-[#94A3B8]">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading articles…
                    </p>
                ) : (
                    <div className="mt-8 overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-sm">
                        {posts.length === 0 && (
                            <p className="py-16 text-center text-sm text-[#94A3B8]">No articles yet. Write your first one.</p>
                        )}
                        {posts.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`admin-post-row-${p.slug}`}
                                className="flex flex-wrap items-center gap-3 border-b border-[#F1F5F9] px-5 py-4 last:border-0 hover:bg-[#FBFDFC]"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-bold text-[#111827]">{p.title}</p>
                                    <p className="mt-0.5 text-xs text-[#94A3B8]">
                                        /blog/{p.slug} · {p.category} · {new Date(p.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                    </p>
                                </div>
                                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                                    p.status === "published" ? "bg-[#F0FDF4] text-[#15803D]" : "bg-amber-50 text-amber-600"
                                }`}>
                                    {p.status}
                                </span>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        onClick={() => toggleStatus(p)}
                                        disabled={busyId === p.id}
                                        data-testid={`admin-toggle-${p.slug}`}
                                        title={p.status === "published" ? "Unpublish" : "Publish"}
                                        className="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3.5 py-1.5 text-xs font-semibold text-[#475569] transition-colors hover:border-[#16A34A]/40 hover:text-[#15803D] disabled:opacity-50"
                                    >
                                        {p.status === "published" ? <FileEdit className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />}
                                        {p.status === "published" ? "Unpublish" : "Publish"}
                                    </button>
                                    <Link
                                        to={`/admin/blog/edit/${p.id}`}
                                        data-testid={`admin-edit-${p.slug}`}
                                        className="flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3.5 py-1.5 text-xs font-semibold text-[#475569] transition-colors hover:border-[#16A34A]/40 hover:text-[#15803D]"
                                    >
                                        <Pencil className="h-3.5 w-3.5" /> Edit
                                    </Link>
                                    <button
                                        onClick={() => remove(p)}
                                        disabled={busyId === p.id}
                                        data-testid={`admin-delete-${p.slug}`}
                                        title="Delete"
                                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E7EB] text-[#94A3B8] transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
